import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Slide,
  useTheme,
  alpha,
  useMediaQuery,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const CommonDialog = ({
  open,
  onClose,
  title,
  subtitle,
  icon: Icon,
  children,
  fullScreen = false,
  maxWidth = "md",
  fullWidth = true,
  noMargin = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDark = theme.palette.mode === "dark";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen || isMobile}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      TransitionComponent={Transition}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(12px) saturate(180%)",
            backgroundColor: alpha("#000000", isDark ? 0.7 : 0.4),
          },
        },
      }}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: fullScreen || isMobile ? 0 : 3,
          background: isDark
            ? alpha(theme.palette.background.paper, 0.95)
            : theme.palette.background.paper,
          backdropFilter: "blur(24px)",
          border: `1px solid ${alpha(isDark ? "#ffffff" : "#000000", 0.08)}`,
          overflow: "hidden",
          boxShadow: isDark
            ? `0 24px 80px ${alpha("#000000", 0.6)}`
            : `0 24px 60px ${alpha("#000000", 0.15)}`,
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2.5,
            background: isDark
              ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`
              : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
            borderBottom: `1px solid ${alpha(isDark ? "#ffffff" : "#000000", 0.06)}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {Icon && (
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.7)} 100%)`,
                  color: "#fff",
                  boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`,
                  flexShrink: 0,
                }}
              >
                <Icon sx={{ fontSize: 22 }} />
              </Box>
            )}
            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ letterSpacing: "-0.02em", lineHeight: 1.3 }}
              >
                {title}
              </Typography>
              {subtitle && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.3 }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              ml: 1,
              flexShrink: 0,
              color: theme.palette.text.secondary,
              bgcolor: alpha(isDark ? "#ffffff" : "#000000", 0.06),
              "&:hover": {
                bgcolor: alpha(theme.palette.error.main, 0.15),
                color: theme.palette.error.main,
                transform: "rotate(90deg)",
              },
              transition: "all 0.3s ease",
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          p: noMargin ? "0 !important" : 3,
          background: isDark
            ? theme.palette.background.paper
            : theme.palette.background.paper,
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-thumb": {
            background: alpha(theme.palette.primary.main, 0.2),
            borderRadius: "3px",
          },
        }}
      >
        <Box mt={noMargin ? 0 : 2}>{children}</Box>
      </DialogContent>
    </Dialog>
  );
};

export default CommonDialog;
